/**
 * PlatformContext - Single Source of Truth for Platform Capabilities
 *
 * Detects what services are available on the current platform:
 * - Electron: Local PostgreSQL, Ollama, pgvector
 * - Web: Cloud APIs (D1, Workers AI, Vectorize)
 *
 * All service hooks (useTransformApi, useAnalyzeApi, etc.) check this
 * context before making any calls.
 *
 * @module @humanizer/studio/contexts/PlatformContext
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type Platform = 'electron' | 'web';

export interface LocalServices {
  /** PostgreSQL database available */
  postgres: {
    available: boolean;
    configured: boolean;
    error?: string;
  };
  /** Ollama LLM service available */
  ollama: {
    available: boolean;
    configured: boolean;
    models: string[];
    error?: string;
  };
}

export interface CloudServices {
  /** User is authenticated to cloud */
  authenticated: boolean;
  /** Cloud API is reachable */
  reachable: boolean;
  /** User's subscription tier */
  tier?: 'free' | 'member' | 'pro' | 'premium' | 'admin';
  /** Whether the local profile is linked to a cloud account (Electron only) */
  cloudLinked?: boolean;
  /** Cloud account email (Electron only) */
  cloudEmail?: string;
}

export interface ByokServices {
  /** Providers with configured BYOK keys */
  availableProviders: string[];
}

export interface PlatformCapabilities {
  // Core platform
  platform: Platform;
  isElectron: boolean;
  isWeb: boolean;

  // Service availability
  local: LocalServices;
  cloud: CloudServices;
  byok: ByokServices;

  // Computed capabilities
  canUseLocalLLM: boolean;
  canUseCloudLLM: boolean;
  canUseByokLLM: boolean;
  canUseLocalArchive: boolean;
  canUseCloudArchive: boolean;

  // Preferred backends (for automatic routing)
  preferredLLMBackend: 'local' | 'cloud' | 'byok' | 'none';
  preferredArchiveBackend: 'local' | 'cloud' | 'none';
}

export interface PlatformContextValue extends PlatformCapabilities {
  /** Refresh capability detection */
  refresh: () => Promise<void>;
  /** Check if a specific capability is available */
  hasCapability: (cap: 'localLLM' | 'cloudLLM' | 'byokLLM' | 'localArchive' | 'cloudArchive') => boolean;
  /** Get reason why a capability is unavailable */
  getUnavailableReason: (cap: 'localLLM' | 'cloudLLM' | 'byokLLM' | 'localArchive' | 'cloudArchive') => string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const PlatformContext = createContext<PlatformContextValue | null>(null);

/**
 * Hook to access platform capabilities
 */
export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    throw new Error('usePlatform must be used within PlatformProvider');
  }
  return ctx;
}

/**
 * Hook to check platform capabilities (safe - returns defaults if no provider)
 */
export function usePlatformOptional(): PlatformContextValue | null {
  return useContext(PlatformContext);
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

interface PlatformProviderProps {
  children: ReactNode;
  /** Override cloud auth status (from AuthContext) */
  isAuthenticated?: boolean;
}

export function PlatformProvider({ children, isAuthenticated = false }: PlatformProviderProps) {
  // Detect platform (stable - never changes)
  const platform: Platform = useMemo(() => {
    return typeof window !== 'undefined' && !!window.isElectron ? 'electron' : 'web';
  }, []);

  const isElectron = platform === 'electron';
  const isWeb = platform === 'web';

  // Local services state
  const [local, setLocal] = useState<LocalServices>({
    postgres: { available: false, configured: false },
    ollama: { available: false, configured: false, models: [] },
  });

  // Cloud services state
  const [cloud, setCloud] = useState<CloudServices>({
    authenticated: isAuthenticated,
    reachable: !isElectron, // Assume reachable on web until proven otherwise
  });

  // BYOK services state
  const [byok, setByok] = useState<ByokServices>({
    availableProviders: [],
  });

  // Update cloud auth when prop changes
  useEffect(() => {
    setCloud(prev => ({ ...prev, authenticated: isAuthenticated }));
  }, [isAuthenticated]);

  // Detect local services (Electron only)
  const detectLocalServices = useCallback(async () => {
    if (!isElectron) return;

    const electronArchive = window.electronArchive;
    const electronOllama = window.electronOllama;

    // Check PostgreSQL
    let postgres: LocalServices['postgres'] = { available: false, configured: false };
    if (electronArchive) {
      try {
        const status = await electronArchive.status();
        postgres = {
          available: status.connected,
          configured: status.connected || status.reason !== 'not-configured',
          error: status.error,
        };
      } catch (err) {
        postgres = {
          available: false,
          configured: false,
          error: err instanceof Error ? err.message : 'Failed to check PostgreSQL',
        };
      }
    }

    // Check Ollama
    let ollama: LocalServices['ollama'] = { available: false, configured: false, models: [] };
    if (electronOllama) {
      try {
        const status = await electronOllama.status() as { available: boolean; error?: string; models?: string[] };
        ollama = {
          available: status.available,
          configured: true,
          models: status.models || [],
          error: status.error,
        };
      } catch (err) {
        ollama = {
          available: false,
          configured: false,
          models: [],
          error: err instanceof Error ? err.message : 'Failed to check Ollama',
        };
      }
    }

    setLocal({ postgres, ollama });

    // Check cloud link status via Electron auth system
    const electronCloudAuth = window.electronCloudAuth;
    if (electronCloudAuth) {
      try {
        const status = await electronCloudAuth.getStatus();
        setCloud(prev => ({
          ...prev,
          cloudLinked: status.linked,
          cloudEmail: status.email,
          tier: status.tier,
          authenticated: status.linked && status.hasValidToken,
          reachable: status.linked && status.hasValidToken,
        }));
      } catch {
        // Cloud auth API not available
      }
    }

    // Check BYOK providers
    const electronByok = window.electronByok;
    if (electronByok) {
      try {
        const providers = await electronByok.list();
        setByok({
          availableProviders: providers.map((p: { provider: string }) => p.provider),
        });
      } catch {
        // BYOK API not available
      }
    }
  }, [isElectron]);

  // Initial detection
  useEffect(() => {
    detectLocalServices();
  }, [detectLocalServices]);

  // Listen for cloud auth invalidation from main process (Electron only)
  useEffect(() => {
    if (!isElectron) return;
    const electronCloudAuth = window.electronCloudAuth;
    if (!electronCloudAuth?.onAuthInvalidated) return;

    const unsubscribe = electronCloudAuth.onAuthInvalidated(() => {
      setCloud(prev => ({
        ...prev,
        authenticated: false,
        reachable: false,
      }));
      // Dispatch DOM event so CloudReauthModal (and any other listener) can react
      window.dispatchEvent(new Event('cloud-auth-invalidated'));
    });

    return unsubscribe;
  }, [isElectron]);

  // Compute capabilities
  const canUseLocalLLM = isElectron && local.ollama.available && local.ollama.models.length > 0;
  const canUseCloudLLM = cloud.authenticated && cloud.reachable;
  const canUseByokLLM = isElectron && byok.availableProviders.length > 0;
  const canUseLocalArchive = isElectron && local.postgres.available;
  const canUseCloudArchive = cloud.authenticated && cloud.reachable;

  // Determine preferred backends
  const preferredLLMBackend: 'local' | 'cloud' | 'byok' | 'none' = useMemo(() => {
    if (isElectron) {
      // Electron prefers local, falls back to BYOK, then cloud
      if (canUseLocalLLM) return 'local';
      if (canUseByokLLM) return 'byok';
      if (canUseCloudLLM) return 'cloud';
      return 'none';
    }
    // Web always uses cloud
    if (canUseCloudLLM) return 'cloud';
    return 'none';
  }, [isElectron, canUseLocalLLM, canUseByokLLM, canUseCloudLLM]);

  const preferredArchiveBackend: 'local' | 'cloud' | 'none' = useMemo(() => {
    if (isElectron) {
      // Electron ONLY uses local (never cloud for archive)
      if (canUseLocalArchive) return 'local';
      return 'none';
    }
    // Web uses cloud
    if (canUseCloudArchive) return 'cloud';
    return 'none';
  }, [isElectron, canUseLocalArchive, canUseCloudArchive]);

  // Refresh detection
  const refresh = useCallback(async () => {
    await detectLocalServices();
  }, [detectLocalServices]);

  // Capability check helper
  const hasCapability = useCallback((cap: 'localLLM' | 'cloudLLM' | 'byokLLM' | 'localArchive' | 'cloudArchive'): boolean => {
    switch (cap) {
      case 'localLLM': return canUseLocalLLM;
      case 'cloudLLM': return canUseCloudLLM;
      case 'byokLLM': return canUseByokLLM;
      case 'localArchive': return canUseLocalArchive;
      case 'cloudArchive': return canUseCloudArchive;
      default: return false;
    }
  }, [canUseLocalLLM, canUseCloudLLM, canUseByokLLM, canUseLocalArchive, canUseCloudArchive]);

  // Get unavailable reason
  const getUnavailableReason = useCallback((cap: 'localLLM' | 'cloudLLM' | 'byokLLM' | 'localArchive' | 'cloudArchive'): string | null => {
    switch (cap) {
      case 'localLLM':
        if (!isElectron) return 'Local LLM only available in desktop app';
        if (!local.ollama.configured) return 'Ollama not configured';
        if (!local.ollama.available) return local.ollama.error || 'Ollama not running';
        if (local.ollama.models.length === 0) return 'No models installed in Ollama';
        return null;
      case 'cloudLLM':
        if (!cloud.authenticated) return 'Sign in to use cloud features';
        if (!cloud.reachable) return 'Cloud service unavailable';
        return null;
      case 'byokLLM':
        if (!isElectron) return 'BYOK only available in desktop app';
        if (byok.availableProviders.length === 0) return 'No API keys configured';
        return null;
      case 'localArchive':
        if (!isElectron) return 'Local archive only available in desktop app';
        if (!local.postgres.configured) return 'PostgreSQL not configured';
        if (!local.postgres.available) return local.postgres.error || 'PostgreSQL not running';
        return null;
      case 'cloudArchive':
        if (!cloud.authenticated) return 'Sign in to use cloud archive';
        if (!cloud.reachable) return 'Cloud service unavailable';
        return null;
      default:
        return 'Unknown capability';
    }
  }, [isElectron, local, cloud, byok]);

  // Memoize context value
  const value = useMemo<PlatformContextValue>(() => ({
    platform,
    isElectron,
    isWeb,
    local,
    cloud,
    byok,
    canUseLocalLLM,
    canUseCloudLLM,
    canUseByokLLM,
    canUseLocalArchive,
    canUseCloudArchive,
    preferredLLMBackend,
    preferredArchiveBackend,
    refresh,
    hasCapability,
    getUnavailableReason,
  }), [
    platform, isElectron, isWeb, local, cloud, byok,
    canUseLocalLLM, canUseCloudLLM, canUseByokLLM, canUseLocalArchive, canUseCloudArchive,
    preferredLLMBackend, preferredArchiveBackend,
    refresh, hasCapability, getUnavailableReason,
  ]);

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}

export default PlatformContext;
